import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { X, Plus, Edit2, Trash2, ArrowLeft } from 'react-feather';
import { ToolbarButton, ConfirmPopover, ConfigEmptyState } from '../../components/ConfigPages';
import { InlineSelect } from '../../components/InlineTable';
import {
  createDepartment, updateDepartment, deleteDepartment,
  createDepartmentCoaMapping, updateDepartmentCoaMapping, deleteDepartmentCoaMapping,
} from '../../utils/departmentApi';

function DepartmentRow({ dept, positionCount, locationId, coaOptions, coaValue, onCoaChange, onEdit, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <tr className="cp-row">
      <td style={{ minWidth: 200 }}>
        <div className="cp-cell">
          <div className="cp-name">{dept.name}</div>
          {dept.code ? <div className="cp-code">{dept.code}</div> : null}
        </div>
      </td>
      <td style={{ width: 96 }}>
        <div className="cp-cell cp-mono" style={{ color: positionCount ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-400)' }}>{positionCount}</div>
      </td>
      {locationId ? (
        <td style={{ minWidth: 300 }}>
          <InlineSelect rowActive value={coaValue} options={coaOptions} onChange={onCoaChange} placeholder="Chart of account…" minWidth={320} ariaLabel="Chart of account" />
        </td>
      ) : null}
      <td style={{ width: 90 }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', paddingRight: 8 }}>
          <div className="cp-row-actions">
            <ToolbarButton small iconOnly variant="ghost" icon={Edit2} title="Edit department" aria-label="Edit department" onClick={() => onEdit(dept)} />
            <ToolbarButton small iconOnly variant="ghost" icon={Trash2} title="Delete department" aria-label="Delete department" onClick={() => setConfirm(true)} />
          </div>
          <ConfirmPopover
            open={confirm}
            onClose={() => setConfirm(false)}
            busy={busy}
            title={`Delete ${dept.name}?`}
            body={positionCount > 0 ? `${positionCount} position${positionCount !== 1 ? 's' : ''} point at it and will need a new department.` : 'No positions use it.'}
            onConfirm={async () => { setBusy(true); try { await onDelete(dept); } finally { setBusy(false); setConfirm(false); } }}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Department modal — list / create / edit / delete departments, and (when a
 * location is chosen) each department's chart-of-account mapping at that
 * location. The location is the page's ONE `selectedLocationId`.
 */
export default function DepartmentModal({
  open, initialMode = 'list', onClose,
  orgId, departments, setDepartments, positions,
  locationOptions, locationId, setLocationId,
  coaOptions, coaMappings, setCoaMappings,
  onDepartmentsChanged,
}) {
  const [mode, setMode] = useState('list'); // list | create | edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setEditing(null);
    setForm({ name: '', code: '' });
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const live = positions.filter((p) => !p.isDeleted);
  const startEdit = (dept) => { setEditing(dept); setForm({ name: dept.name || '', code: dept.code || '' }); setMode('edit'); };
  const startCreate = () => { setEditing(null); setForm({ name: '', code: '' }); setMode('create'); };
  const backToList = () => { setMode('list'); setEditing(null); setForm({ name: '', code: '' }); };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { toast.error('Department name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && editing) {
        await updateDepartment(editing.id, { name, code: form.code.trim() || null, orgId: editing.orgId });
        setDepartments((prev) => prev.map((d) => (d.id === editing.id ? { ...d, name, code: form.code.trim() || null } : d)).sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Saved');
      } else {
        const created = await createDepartment({ name, code: form.code.trim() || null, orgId });
        setDepartments((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Created ${name}`);
      }
      backToList();
    } catch (err) {
      console.error('Error saving department:', err);
      toast.error(`Could not ${mode === 'edit' ? 'save' : 'create'} the department — try again`);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (dept) => {
    try {
      await deleteDepartment(dept.id);
      setDepartments((prev) => prev.filter((d) => d.id !== dept.id));
      toast.success(`Deleted ${dept.name}`);
      // deleteDepartment clears the department cache only — positions still
      // carry the old FK; refetch so the Positions tab is truthful.
      if (onDepartmentsChanged) await onDepartmentsChanged();
    } catch (err) {
      console.error('Error deleting department:', err);
      toast.error(`Could not delete ${dept.name} — try again`);
    }
  };

  const mappingFor = (departmentId) => coaMappings.find((m) => m.departmentId === departmentId);
  const onCoaChange = async (departmentId, chartOfAccountId) => {
    if (!locationId || !orgId) return;
    const existing = mappingFor(departmentId);
    try {
      if (existing && chartOfAccountId != null) {
        await updateDepartmentCoaMapping(existing.id, chartOfAccountId);
        setCoaMappings((prev) => prev.map((m) => (m.id === existing.id ? { ...m, chartOfAccountId } : m)));
        toast.success('Chart of account updated');
      } else if (existing && chartOfAccountId == null) {
        await deleteDepartmentCoaMapping(existing.id);
        setCoaMappings((prev) => prev.filter((m) => m.id !== existing.id));
        toast.success('Chart of account cleared');
      } else if (!existing && chartOfAccountId != null) {
        const created = await createDepartmentCoaMapping({ orgId, locationId, departmentId, chartOfAccountId });
        setCoaMappings((prev) => [...prev, created]);
        toast.success('Chart of account set');
      }
    } catch (err) {
      console.error('Error updating department COA mapping:', err);
      toast.error('Could not save the chart of account — try again');
    }
  };

  const locationName = locationOptions.find((o) => o.value === locationId)?.label;
  const title = mode === 'create' ? 'Add department' : mode === 'edit' ? `Edit ${editing?.name || 'department'}` : 'Departments';

  return (
    <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`cp-modal${mode === 'list' ? ' cp-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="cp-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {mode !== 'list' && initialMode === 'list' ? <ToolbarButton small iconOnly variant="ghost" icon={ArrowLeft} onClick={backToList} aria-label="Back to departments" /> : null}
            <h2 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h2>
          </div>
          <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={onClose} aria-label="Close" />
        </div>

        {mode === 'list' ? (
          <>
            <div className="cp-toolbar">
              <div className="cp-scope">
                <span className="cp-scope-label">Location</span>
                <div style={{ width: 240 }}>
                  <InlineSelect rowActive value={locationId} options={locationOptions} onChange={(v) => setLocationId(v ?? null)} placeholder="Choose for COA mapping…" minWidth={240} ariaLabel="Location for chart-of-account mapping" />
                </div>
              </div>
              <div className="cp-help">{locationId ? `Mapping each department to its account at ${locationName}.` : 'Pick a location to map departments to chart-of-account lines.'}</div>
              <div className="cp-toolbar-spacer" />
              <div className="cp-meta">{departments.length} departments</div>
              <ToolbarButton variant="primary" icon={Plus} onClick={startCreate}>Add department</ToolbarButton>
            </div>
            {departments.length === 0 ? (
              <ConfigEmptyState title="No departments yet" body="Departments group positions for the labor dashboards. Add the first one." action={<ToolbarButton variant="primary" icon={Plus} onClick={startCreate}>Add department</ToolbarButton>} />
            ) : (
              <div className="it-scroll" style={{ '--it-max-h': '60vh' }}>
                <table className="it-tbl">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th style={{ width: 96 }}>Positions</th>
                      {locationId ? <th style={{ minWidth: 300 }}>Chart of account · {locationName}</th> : null}
                      <th style={{ width: 90 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((d) => (
                      <DepartmentRow
                        key={d.id}
                        dept={d}
                        positionCount={live.filter((p) => p.departmentId === d.id).length}
                        locationId={locationId}
                        coaOptions={coaOptions}
                        coaValue={mappingFor(d.id)?.chartOfAccountId ?? null}
                        onCoaChange={(v) => onCoaChange(d.id, v ?? null)}
                        onEdit={startEdit}
                        onDelete={onDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="cp-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label className="cp-field">
                <span className="cp-label">Name</span>
                <input className="cp-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Front office" required autoFocus />
              </label>
              <label className="cp-field">
                <span className="cp-label">Code</span>
                <input className="cp-input cp-mono" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="Optional short code" />
                <span className="cp-help">Optional. Handy when the payroll feed carries a department code.</span>
              </label>
            </div>
            <div className="cp-modal-foot">
              <ToolbarButton variant="ghost" onClick={initialMode === 'list' ? backToList : onClose} disabled={saving}>Cancel</ToolbarButton>
              <ToolbarButton variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create'}</ToolbarButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
